import React, { useEffect, useState } from 'react';

interface TrainingTimerProps {
  startTime?: number; // When the session started (timestamp)
  isActive?: boolean; // Whether the timer should be running
  timeRemaining?: number; // Time remaining in milliseconds
  className?: string;
}

export const TrainingTimer: React.FC<TrainingTimerProps> = ({
  startTime = Date.now(),
  isActive = false,
  timeRemaining = 60000,
  className = ''
}) => {
  const [elapsedTime, setElapsedTime] = useState(0);

  useEffect(() => {
    if (!isActive) return;

    const interval = setInterval(() => {
      const now = Date.now();
      const elapsed = now - startTime;
      setElapsedTime(elapsed);
    }, 100); // Update every 100ms for smooth display

    return () => clearInterval(interval);
  }, [isActive, startTime]);

  /**
   * Format time in MM:SS.T format (minutes:seconds.tenths)
   */
  const formatTime = (milliseconds: number): string => {
    const totalSeconds = Math.floor(milliseconds / 1000);
    const minutes = Math.floor(totalSeconds / 60);
    const seconds = totalSeconds % 60;
    const tenths = Math.floor((milliseconds % 1000) / 100);
    
    return `${minutes.toString().padStart(2, '0')}:${seconds.toString().padStart(2, '0')}.${tenths}`;
  };

  /**
   * Format remaining time in MM:SS format
   */
  const formatRemainingTime = (milliseconds: number): string => {
    const totalSeconds = Math.ceil(milliseconds / 1000);
    const minutes = Math.floor(totalSeconds / 60);
    const seconds = totalSeconds % 60;
    
    return `${minutes.toString().padStart(2, '0')}:${seconds.toString().padStart(2, '0')}`;
  };

  /**
   * Calculate progress percentage
   */
  const getProgressPercentage = (): number => {
    const sessionDuration = 60000; // 1 minute session
    const remaining = Math.max(0, timeRemaining);
    return ((sessionDuration - remaining) / sessionDuration) * 100;
  };

  if (!isActive) {
    return null;
  }

  return (
    <div className={`training-timer ${className}`}>
      <div className="timer-content">
        {/* Elapsed Time */}
        <div className="timer-section elapsed">
          <div className="timer-label">Elapsed</div>
          <div className="timer-value elapsed-value">
            {formatTime(elapsedTime)}
          </div>
        </div>

        {/* Progress Bar */}
        <div className="timer-progress">
          <div className="progress-track">
            <div 
              className="progress-fill"
              style={{ width: `${getProgressPercentage()}%` }}
            />
          </div>
          <div className="progress-labels">
            <span className="progress-start">0:00</span>
            <span className="progress-end">1:00</span>
          </div>
        </div>

        {/* Remaining Time */}
        <div className="timer-section remaining">
          <div className="timer-label">Remaining</div>
          <div className="timer-value remaining-value">
            {formatRemainingTime(timeRemaining)}
          </div>
        </div>
      </div>

      <style>{`
        .training-timer {
          position: fixed;
          bottom: 20px;
          left: 50%;
          transform: translateX(-50%);
          z-index: 200; /* Above everything */
          background: rgba(17, 24, 39, 0.95);
          border: 1px solid rgba(75, 85, 99, 0.5);
          border-radius: 12px;
          backdrop-filter: blur(20px);
          padding: 12px 24px;
          min-width: 400px;
          box-shadow: 0 8px 25px rgba(0, 0, 0, 0.3);
        }

        .timer-content {
          display: flex;
          align-items: center;
          gap: 24px;
        }

        .timer-section {
          display: flex;
          flex-direction: column;
          align-items: center;
          gap: 4px;
          min-width: 80px;
        }

        .timer-label {
          font-size: 0.75rem;
          color: #9ca3af;
          font-weight: 500;
          text-transform: uppercase;
          letter-spacing: 0.05em;
        }

        .timer-value {
          font-size: 1.25rem;
          font-weight: 700;
          font-family: 'Courier New', monospace;
          text-align: center;
        }

        .elapsed-value {
          color: #3b82f6;
        }

        .remaining-value {
          color: #f59e0b;
        }

        .timer-progress {
          flex: 1;
          display: flex;
          flex-direction: column;
          gap: 4px;
        }

        .progress-track {
          width: 100%;
          height: 8px;
          background: rgba(75, 85, 99, 0.3);
          border-radius: 4px;
          overflow: hidden;
          position: relative;
        }

        .progress-fill {
          height: 100%;
          background: linear-gradient(90deg, #3b82f6, #22c55e);
          border-radius: 4px;
          transition: width 0.2s ease-out;
          box-shadow: 0 0 8px rgba(59, 130, 246, 0.4);
        }

        .progress-labels {
          display: flex;
          justify-content: space-between;
          font-size: 0.625rem;
          color: #6b7280;
          font-family: 'Courier New', monospace;
        }

        /* Animation for low time warning */
        @keyframes urgentPulse {
          0%, 100% { 
            border-color: rgba(239, 68, 68, 0.5);
            box-shadow: 0 8px 25px rgba(0, 0, 0, 0.3);
          }
          50% { 
            border-color: rgba(239, 68, 68, 0.8);
            box-shadow: 0 8px 25px rgba(239, 68, 68, 0.3);
          }
        }

        /* Apply urgent animation when less than 10 seconds remaining */
        .training-timer:has(.remaining-value:contains("0:0")) {
          animation: urgentPulse 1s ease-in-out infinite;
        }

        /* Responsive design */
        @media (max-width: 768px) {
          .training-timer {
            min-width: 320px;
            padding: 10px 20px;
            bottom: 10px;
          }

          .timer-content {
            gap: 16px;
          }

          .timer-section {
            min-width: 60px;
          }

          .timer-value {
            font-size: 1rem;
          }

          .timer-label {
            font-size: 0.675rem;
          }
        }

        @media (max-width: 480px) {
          .training-timer {
            min-width: 280px;
            padding: 8px 16px;
          }

          .timer-content {
            gap: 12px;
          }

          .timer-section {
            min-width: 50px;
          }

          .timer-value {
            font-size: 0.875rem;
          }
        }

        /* Smooth entrance animation */
        @keyframes timerEntrance {
          from {
            transform: translateX(-50%) translateY(100%);
            opacity: 0;
          }
          to {
            transform: translateX(-50%) translateY(0);
            opacity: 1;
          }
        }

        .training-timer {
          animation: timerEntrance 0.3s ease-out;
        }
      `}</style>
    </div>
  );
};

export default TrainingTimer;
